import { useDeferredValue, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FolderKanban,
  Plus,
  Search,
} from "lucide-react";

import { Button } from "../../components/Button/Button";
import { Card } from "../../components/Card/Card";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { Input } from "../../components/Input/Input";
import { Badge } from "../../components/Badge/Badge";
import { Menu, MenuItem } from "../../components/Menu/Menu";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { useToast } from "../../components/Toast/Toast";
import { gradientFor, initialsFor } from "../../components/Avatar/Avatar";
import { useAuth, ProjectListItem } from "../auth/useAuth";
import {
  listProjects,
  suspendProject,
  unsuspendProject,
  CreateProjectResponse,
} from "./api";
import { ApiError, describeError } from "../../lib/errors";
import { CreateProjectModal } from "./CreateProjectModal";
import { RevealApiKeyModal } from "./RevealApiKeyModal";
import styles from "./ProjectsListPage.module.css";

type ProjectStatus = "active" | "setup" | "suspended";
type StatusFilter = "all" | ProjectStatus;
type SortKey = "activity" | "created" | "name";

const PAGE_SIZE = 20;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "setup", label: "Setup" },
  { value: "suspended", label: "Suspended" },
];

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "activity", label: "Recently active" },
  { value: "created", label: "Recently created" },
  { value: "name", label: "Alphabetical" },
];

/**
 * Derive the visual status from a project's data. "suspended" is the stored
 * server status; "setup" is a UI-only derived state (active + no activity yet);
 * everything else is "active".
 */
export function deriveStatus(p: ProjectListItem): ProjectStatus {
  if (p.status === "suspended") return "suspended";
  if (!p.last_activity_at) return "setup";
  return "active";
}

/** Short relative-time formatter — "just now", "2h ago", "3d ago", "1w ago", "Apr 22". */
export function humanizeRelativeDate(
  iso: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = now.getTime() - t;
  if (diff < 0) return "just now";
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 4) return `${wk}w ago`;
  // Older than ~a month — fall back to absolute month + day, no year.
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(new Date(iso));
}

/** Long absolute date — "Mar 14, 2024". */
export function formatCreatedDate(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(new Date(iso));
}

/** Grouped player count — "12,847". Zero renders as "0", never a dash. */
export function formatPlayers(count: number): string {
  return count.toLocaleString("en-US");
}

/** "e74c…a82f" — first 4 chars of UUID + ellipsis + last 4. */
export function truncateId(id: string): string {
  const clean = id.replace(/-/g, "");
  if (clean.length < 9) return id;
  return `${clean.slice(0, 4)}…${clean.slice(-4)}`;
}

interface ProjectIconProps {
  name: string;
  size?: number;
}
function ProjectIcon({ name, size = 32 }: ProjectIconProps) {
  const initials = initialsFor(name).slice(0, 1);
  const fontSize = Math.max(11, Math.round(size * 0.42));
  return (
    <span
      className={styles.projectIcon}
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        fontSize,
        background: gradientFor(name),
      }}
    >
      {initials}
    </span>
  );
}

interface FilterChipProps {
  label: string;
  value: string;
  items: MenuItem[];
}
function FilterChip({ label, value, items }: FilterChipProps) {
  return (
    <Menu
      items={items}
      placement="bottom-start"
      aria-label={`${label} filter`}
      trigger={({ open, toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`${label} filter — ${value}`}
          className={styles.filterChip}
          onClick={toggle}
        >
          <span className={styles.filterChipLabel}>{label}:</span>
          <span className={styles.filterChipValue}>{value}</span>
          <ChevronDown
            width={11}
            height={11}
            aria-hidden="true"
            className={styles.filterChipIcon}
          />
        </button>
      )}
    />
  );
}

export function ProjectsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { push } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState<CreateProjectResponse | null>(null);

  // Toolbar state — client-side filter / sort / page.
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [status, setStatus] = useState<StatusFilter>("all");
  const [sort, setSort] = useState<SortKey>("activity");
  const [page, setPage] = useState(1);

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: listProjects,
  });

  const isSuper = user?.role === "super_admin";

  function onStatusError(err: unknown) {
    push({
      variant: "danger",
      message:
        err instanceof ApiError
          ? describeError(err.code)
          : describeError("internal"),
    });
  }

  const suspendMut = useMutation({
    mutationFn: (id: string) => suspendProject(id),
    onSuccess: async (_data, projectId) => {
      push({ variant: "success", message: "Project suspended." });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await qc.invalidateQueries({
        queryKey: ["project", projectId, "activity"],
      });
    },
    onError: onStatusError,
  });

  const unsuspendMut = useMutation({
    mutationFn: (id: string) => unsuspendProject(id),
    onSuccess: async (_data, projectId) => {
      push({ variant: "success", message: "Project reactivated." });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await qc.invalidateQueries({
        queryKey: ["project", projectId, "activity"],
      });
    },
    onError: onStatusError,
  });

  // Apply client-side filtering + sort.
  const filtered = useMemo(() => {
    const q = deferredQuery.trim().toLowerCase();
    let rows = projects.slice();
    if (q) rows = rows.filter((p) => p.name.toLowerCase().includes(q));
    if (status !== "all") {
      rows = rows.filter((p) => deriveStatus(p) === status);
    }
    rows.sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "created") {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      }
      // activity (default) — recently active first; nulls last.
      const at = a.last_activity_at
        ? new Date(a.last_activity_at).getTime()
        : -Infinity;
      const bt = b.last_activity_at
        ? new Date(b.last_activity_at).getTime()
        : -Infinity;
      return bt - at;
    });
    return rows;
  }, [projects, deferredQuery, status, sort]);

  // Clamp page to a valid range whenever the filter result size changes.
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  function rowMenu(p: ProjectListItem): MenuItem[] {
    const items: MenuItem[] = [
      {
        label: "Open project",
        onSelect: () => navigate(`/projects/${p.id}/overview`),
      },
      {
        label: "Copy ID",
        onSelect: () => {
          void navigator.clipboard?.writeText(p.id).catch(() => undefined);
        },
      },
    ];
    if (isSuper) {
      items.push({
        label: "Rename",
        onSelect: () => navigate(`/projects/${p.id}/overview`),
      });
      if (p.status === "suspended") {
        items.push({
          label: "Unsuspend",
          onSelect: () => unsuspendMut.mutate(p.id),
        });
      } else {
        items.push({
          label: "Suspend",
          variant: "danger",
          onSelect: () => suspendMut.mutate(p.id),
        });
      }
    }
    return items;
  }

  const title = isSuper ? "All projects" : "My projects";
  const breadcrumb = [{ label: "Platform" }, { label: title }];

  const showEmptyState = !isLoading && projects.length === 0;
  const showNoMatches = !isLoading && projects.length > 0 && filtered.length === 0;

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={title}
        actions={
          isSuper ? (
            <Button onClick={() => setCreateOpen(true)} icon={<Plus size={14} />}>
              Create project
            </Button>
          ) : undefined
        }
      />
      <div className={styles.page}>
        <div className={styles.body}>
        {showEmptyState ? (
          <ProjectsEmptyState
            isSuper={!!isSuper}
            onCreate={() => setCreateOpen(true)}
          />
        ) : (
          <>
            <div className={styles.toolbar}>
              <div className={styles.search}>
                <Input
                  type="search"
                  aria-label="Search projects"
                  placeholder={
                    isSuper ? "Search projects by name…" : "Search…"
                  }
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                  leadingIcon={<Search size={14} aria-hidden="true" />}
                />
              </div>
              {isSuper && (
                <FilterChip
                  label="Status"
                  value={
                    STATUS_OPTIONS.find((o) => o.value === status)?.label ??
                    "All"
                  }
                  items={STATUS_OPTIONS.map((o) => ({
                    label: o.label,
                    onSelect: () => {
                      setStatus(o.value);
                      setPage(1);
                    },
                  }))}
                />
              )}
              {isSuper && (
                <FilterChip
                  label="Sort"
                  value={
                    SORT_OPTIONS.find((o) => o.value === sort)?.label ??
                    "Recently active"
                  }
                  items={SORT_OPTIONS.map((o) => ({
                    label: o.label,
                    onSelect: () => setSort(o.value),
                  }))}
                />
              )}
            </div>

            <Card className={styles.tableCard}>
              {isLoading ? (
                <div className={styles.loading}>Loading…</div>
              ) : showNoMatches ? (
                <div className={styles.noMatches}>
                  No matches for «{deferredQuery.trim()}».
                </div>
              ) : isSuper ? (
                <SuperAdminTable
                  rows={pageRows}
                  onRowClick={(p) =>
                    navigate(`/projects/${p.id}/overview`)
                  }
                  rowMenu={rowMenu}
                />
              ) : (
                <MemberTable
                  rows={pageRows}
                  onRowClick={(p) =>
                    navigate(`/projects/${p.id}/overview`)
                  }
                />
              )}
            </Card>

            {pageCount > 1 && (
              <div className={styles.pagination}>
                <div>
                  Showing {startIdx + 1}–{startIdx + pageRows.length} of{" "}
                  {filtered.length} projects
                </div>
                <div className={styles.pages}>
                  <button
                    type="button"
                    className={styles.pageBtn}
                    aria-label="Previous page"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft size={12} aria-hidden="true" />
                  </button>
                  <span className={styles.pageIndicator}>
                    {currentPage} / {pageCount}
                  </span>
                  <button
                    type="button"
                    className={styles.pageBtn}
                    aria-label="Next page"
                    disabled={currentPage >= pageCount}
                    onClick={() =>
                      setPage((p) => Math.min(pageCount, p + 1))
                    }
                  >
                    <ChevronRight size={12} aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <CreateProjectModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(r) => {
          setCreateOpen(false);
          setRevealed(r);
        }}
      />

      <RevealApiKeyModal
        result={revealed}
        onStayOnList={() => setRevealed(null)}
        onOpenProject={(id) => {
          setRevealed(null);
          navigate(`/projects/${id}/overview`);
        }}
      />
      </div>
    </>
  );
}

interface SuperAdminTableProps {
  rows: ProjectListItem[];
  onRowClick: (p: ProjectListItem) => void;
  rowMenu: (p: ProjectListItem) => MenuItem[];
}
function SuperAdminTable({ rows, onRowClick, rowMenu }: SuperAdminTableProps) {
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th style={{ width: "42%" }}>Project</th>
            <th>Created</th>
            <th>Players</th>
            <th>Last activity</th>
            <th>Status</th>
            <th style={{ width: 60 }} aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const st = deriveStatus(p);
            return (
              <tr
                key={p.id}
                className={`${styles.row} ${
                  st === "suspended" ? styles.rowSuspended : ""
                }`}
                onClick={() => onRowClick(p)}
                tabIndex={0}
                role="button"
                aria-label={`Open project ${p.name}`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onRowClick(p);
                  }
                }}
              >
                <td>
                  <div className={styles.projectCell}>
                    <ProjectIcon name={p.name} />
                    <span className={styles.projectCellName}>
                      <span className={styles.name}>{p.name}</span>
                      <span className={styles.sub}>{truncateId(p.id)}</span>
                    </span>
                  </div>
                </td>
                <td className={styles.muted}>
                  {formatCreatedDate(p.created_at)}
                </td>
                <td className={styles.num}>{formatPlayers(p.players_count)}</td>
                <td className={styles.muted}>
                  {humanizeRelativeDate(p.last_activity_at)}
                </td>
                <td>
                  <StatusBadge status={st} />
                </td>
                <td
                  className={styles.menuCell}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Menu items={rowMenu(p)} aria-label="Row actions" />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface MemberTableProps {
  rows: ProjectListItem[];
  onRowClick: (p: ProjectListItem) => void;
}
function MemberTable({ rows, onRowClick }: MemberTableProps) {
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th style={{ width: "50%" }}>Project</th>
            <th>Joined</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const st = deriveStatus(p);
            return (
              <tr
                key={p.id}
                className={`${styles.row} ${
                  st === "suspended" ? styles.rowSuspended : ""
                }`}
                onClick={() => onRowClick(p)}
                tabIndex={0}
                role="button"
                aria-label={`Open project ${p.name}`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onRowClick(p);
                  }
                }}
              >
                <td>
                  <div className={styles.projectCell}>
                    <ProjectIcon name={p.name} />
                    <span className={styles.projectCellName}>
                      <span className={styles.name}>{p.name}</span>
                      <span className={styles.subPlain}>
                        {formatPlayers(p.players_count)}{" "}
                        {p.players_count === 1 ? "player" : "players"}
                      </span>
                    </span>
                  </div>
                </td>
                <td className={styles.muted}>
                  {/* No server-side membership join date yet — fall back to created. */}
                  {formatCreatedDate(p.created_at)}
                </td>
                <td>
                  <StatusBadge status={st} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }: { status: ProjectStatus }) {
  if (status === "active")
    return <Badge variant="success">Active</Badge>;
  if (status === "setup") return <Badge variant="warn">Setup</Badge>;
  return <Badge variant="danger">Suspended</Badge>;
}

interface EmptyProps {
  isSuper: boolean;
  onCreate: () => void;
}
function ProjectsEmptyState({ isSuper, onCreate }: EmptyProps) {
  if (isSuper) {
    return (
      <div className={styles.emptyWrap}>
        <EmptyState
          icon={<FolderKanban width={24} height={24} aria-hidden="true" />}
          title="No projects yet"
          description="Register your first game project to start using Social Hub. You'll get a Game ID for your Unity client and a server API key for your backend."
          action={
            <Button onClick={onCreate} icon={<Plus size={14} />}>
              Create your first project
            </Button>
          }
        />
        <div className={styles.emptyHelper}>
          Or invite a Member to an existing project via{" "}
          <a className={styles.link} href="/users">
            Platform users
          </a>
          .
        </div>
      </div>
    );
  }
  return (
    <div className={styles.emptyWrap}>
      <EmptyState
        icon={<FolderKanban width={24} height={24} aria-hidden="true" />}
        title="No projects yet"
        description="You don't have access to any projects yet. Ask an admin for an invite, or check your Invites for pending ones."
      />
      <div className={styles.emptyHelper}>
        Check your{" "}
        <a className={styles.link} href="/invites">
          Invites
        </a>{" "}
        for pending invitations.
      </div>
    </div>
  );
}
